// Loads the content saved in the admin and applies it on top of content.js.
// content.js stays the starting version and the fallback: if the server
// cannot be reached, the site shows content.js as it is.
import * as content from './content';

export const EDITABLE_KEYS = [
  'brand', 'contact', 'nav', 'interests', 'footer',
  'home', 'clients', 'quote',
  'whatWeDo', 'services',
  'howWeWork', 'network',
  'partner', 'academy', 'about', 'contactPage',
  'storiesPage', 'stories',
];

const clone = (v) => JSON.parse(JSON.stringify(v));

// A copy of content.js exactly as shipped, before any saved edits.
export const DEFAULTS = clone(Object.fromEntries(EDITABLE_KEYS.map((k) => [k, content[k]])));

let API_BASE = '';
try { API_BASE = process.env.REACT_APP_BACKEND_URL || ''; } catch (e) { API_BASE = ''; }
export const api = (path) => `${API_BASE}/api${path}`;

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

// Copy saved values into the live objects exported by content.js, so every
// page that imports content.js shows the saved version.
function applyInto(target, saved) {
  Object.keys(saved).forEach((k) => {
    const v = saved[k];
    if (Array.isArray(target[k]) && Array.isArray(v)) {
      target[k].splice(0, target[k].length, ...mergeArray(target[k], v));
    } else if (isObj(target[k]) && isObj(v)) {
      applyInto(target[k], v);
    } else {
      target[k] = clone(v);
    }
  });
}

// Saved list items are laid over the matching shipped item (same slug, name
// or title; otherwise the same position), so a field added to content.js
// later still has its starting value, even if the list was reordered.
const idOf = (it) => (isObj(it) ? it.slug || it.name || it.title || null : null);
function mergeArray(base, saved) {
  return saved.map((item, i) => {
    if (!isObj(item)) return clone(item);
    const id = idOf(item);
    let match = id ? base.find((b) => idOf(b) === id) : null;
    if (!match && isObj(base[i]) && (!id || !idOf(base[i]))) match = base[i];
    if (!match) return clone(item);
    const out = clone(match);
    applyInto(out, item);
    return out;
  });
}

export function applyContent(data) {
  if (!isObj(data)) return;
  EDITABLE_KEYS.forEach((key) => {
    if (!(key in data)) return;
    const live = content[key];
    const v = data[key];
    if (Array.isArray(live) && Array.isArray(v)) live.splice(0, live.length, ...mergeArray(live, v));
    else if (isObj(live) && isObj(v)) applyInto(live, v);
  });
}

// The current live content (after saved edits), as one plain object.
export function currentContent() {
  return clone(Object.fromEntries(EDITABLE_KEYS.map((k) => [k, content[k]])));
}

export async function loadLiveContent(timeoutMs = 2500) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => ctrl && ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(api('/content'), { signal: ctrl ? ctrl.signal : undefined, cache: 'no-store' });
    if (res.ok) {
      const body = await res.json();
      if (body && body.data) applyContent(body.data);
    }
  } catch (e) {
    // Server not reachable: keep content.js as it is.
  } finally {
    clearTimeout(timer);
  }
}
